/**
 * Copyright IBM Corp. 2022, 2026
 */

import type { TextBlockHeadingElement, TextBlockSize } from './types'

export const VALID_SIZES = ['small', 'medium', 'large'] as const
export const VALID_HEADING_ELEMENTS = [
	'h1',
	'h2',
	'h3',
	'h4',
	'h5',
	'h6',
] as const
export const VALID_ALIGNMENTS = ['left', 'center'] as const

/**
 * Utility function that converts a string into a valid HTML heading element.
 * This function ensures that a valid string is passed in. It also allows us
 * to narrow our type from a string to a string literal.
 *
 * If an invalid string value is passed in, we fallback to `h2`.
 *
 * @param {string} headingElementStr - string of an HTML heading element
 *
 * @returns {HeadingElement} valid HTML heading element
 */
export const getSize = (sizeStr: string): TextBlockSize => {
	const size = sizeStr as TextBlockSize
	return VALID_SIZES.includes(size) ? size : 'medium'
}

export const getHeadingElement = (
	headingElementStr: string,
): TextBlockHeadingElement => {
	return VALID_HEADING_ELEMENTS.includes(
		headingElementStr as TextBlockHeadingElement,
	)
		? (headingElementStr as TextBlockHeadingElement)
		: 'h2'
}

export const getAlignment = (alignmentStr: string) => {
	return VALID_ALIGNMENTS.includes(alignmentStr as any)
		? (alignmentStr as any)
		: 'left'
}
